import { AUTH_TAB } from '@invideoio/web-shared/components/Auth/constants';
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { AppState } from '@store/store';
import { BaseAppType } from 'src/types';

export enum ModalType {
  CLOSE_PROJECT = 'CLOSE_PROJECT',
  UPLOAD = 'UPLOAD',
  EXPORT_LOADING = 'EXPORT_LOADING',
  NONE = 'NONE',
}

export enum SharedModalType {
  AUTH = 'AUTH',
  TEAM_INVITE = 'INVITE',
  NONE = 'NONE',
}

export enum PREVIEW_SCREENS {
  UPLOAD_SCREEN = 'UPLOAD_SCREEN',
  EDITOR_SCREEN = 'EDITOR_SCREEN',
  PREVIEW_SCREEN = 'PREVIEW_SCREEN',
  DOWNLOAD_SCREEN = 'DOWNLOAD_SCREEN',
}

export type ShellState = {
  modalState: {
    activeModal: ModalType;
  };
  sharedModalState: {
    activeModal: SharedModalType;
    authModal: { state: boolean; openTab: AUTH_TAB };
    redirectionLink: string;
    authCallBack: null | Function;
  };
  editorScreen: PREVIEW_SCREENS;
  baseAppType: BaseAppType;
};

// INITIAL STATE
const initialUserState: ShellState = {
  modalState: {
    activeModal: ModalType.NONE,
  },
  sharedModalState: {
    activeModal: SharedModalType.NONE,
    authModal: { state: false, openTab: AUTH_TAB.OTP_LOGIN },
    redirectionLink: '',
    authCallBack: null,
  },
  editorScreen: PREVIEW_SCREENS.UPLOAD_SCREEN,
  baseAppType: BaseAppType.STUDIO,
};

// REDUCER
const shellSlice = createSlice({
  name: 'app',
  initialState: initialUserState,
  reducers: {
    OPEN_MODAL: (state, action: PayloadAction<ModalType>) => {
      return {
        ...state,
        modalState: { activeModal: action.payload },
      };
    },
    CLOSE_MODAL: state => {
      return {
        ...state,
        modalState: {
          activeModal: ModalType.NONE,
        },
      };
    },
    SET_ACTIVE_MODAL: (state, action: PayloadAction<SharedModalType>) => {
      return {
        ...state,
        sharedModalState: {
          ...state.sharedModalState,
          activeModal: action.payload,
        },
      };
    },
    OPEN_AUTH_MODAL: (state, action: PayloadAction<AUTH_TAB>) => {
      return {
        ...state,
        sharedModalState: {
          ...state.sharedModalState,
          activeModal: SharedModalType.AUTH,
          authModal: { state: true, openTab: action.payload },
          redirectionLink: '',
        },
      };
    },
    SET_REDIRECTION_LINK: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        sharedModalState: {
          ...state.sharedModalState,
          redirectionLink: action.payload,
        },
      };
    },
    SET_AUTH_CALLBACK: (state, action: PayloadAction<Function | null>) => {
      return {
        ...state,
        sharedModalState: {
          ...state.sharedModalState,
          authCallBack: action.payload,
        },
      };
    },
    SET_EDITOR_SCREEN: (state, action: PayloadAction<PREVIEW_SCREENS>) => {
      return {
        ...state,
        editorScreen: action.payload,
      };
    },
    BASE_APP_TYPE_LOADED: (state, action: PayloadAction<BaseAppType>) => {
      return {
        ...state,
        baseAppType: action.payload,
      };
    },
  },
});

export const shellStateSelector = createSelector(
  (state: AppState) => state.shell,
  state => state,
);

export const ModalStateSelector = createSelector(
  (state: AppState) => state.shell,
  state => state.modalState,
);

export const authModalSelector = createSelector(
  (state: AppState) => state.shell,
  state => state.sharedModalState.authModal,
);

export const activeSharedModalSelector = createSelector(
  (state: AppState) => state.shell,
  state => state.sharedModalState.activeModal,
);

export const redirectionLinkSelector = createSelector(
  (state: AppState) => state.shell,
  state => state.sharedModalState.redirectionLink,
);

export const authCallBackSelector = createSelector(
  (state: AppState) => state.shell,
  state => state.sharedModalState.authCallBack,
);

export const getCurrentScreen = createSelector(
  (state: AppState) => state.shell,
  state => state.editorScreen,
);

export const getBaseAppType = createSelector(
  (state: AppState) => state.shell,
  state => state.baseAppType,
);

export const {
  CLOSE_MODAL,
  OPEN_MODAL,
  SET_ACTIVE_MODAL,
  OPEN_AUTH_MODAL,
  SET_REDIRECTION_LINK,
  SET_AUTH_CALLBACK,
  SET_EDITOR_SCREEN,
  BASE_APP_TYPE_LOADED,
} = shellSlice.actions;

export default shellSlice.reducer;
